import {Formik, Form, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {SecurityBase} from './type'
import toast from 'react-hot-toast'
import {createSecurity, updateSecurity} from './api'
import {handleApiError} from '../../../helpers/errorHandler'
import FormikControl from '../../../components/formikControl/FormikControl'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'
import {Col, Row} from 'react-bootstrap'
import {startOfToday} from 'date-fns'
import CountrySelect from '../../../components/CountryDropdown'

interface SecurityFormProps {
  initialValues?: SecurityBase
  onAfterSubmit: () => void
}

const formInitialValues: Omit<SecurityBase, 'security_id'> = {
  security_name: '',
  security_status: '1',
  license_expiry_date: '',
  blood_group: '',
  security_code: '',
  passport: '',
  passport_expiry: '',
  passport_country: '',
  qatar_id: '',
  qatar_id_expiry: '',
}
const validationSchema: Yup.Schema<Omit<SecurityBase, 'security_id'>> = Yup.object({
  security_name: Yup.string()
    .max(100, 'The Security name cannot exceed 100 characters')
    .required('Security Name is required'),
  security_status: Yup.string()
    .oneOf(['0', '1'], 'Security Status must be either "0" or "1"')
    .required('Security Status is required'),
  license_expiry_date: Yup.date()
    .min(startOfToday(), 'License Expiry cannot be in the past')
    .nullable(),
  blood_group: Yup.string().nullable(),
  security_code: Yup.string().nullable(),
  passport: Yup.string().nullable(),
  passport_expiry: Yup.date()
    .min(startOfToday(), 'Passport Expiry cannot be in the past')
    .nullable(),

  passport_country: Yup.string().nullable(),
  qatar_id: Yup.string().nullable(),
  qatar_id_expiry: Yup.date()
    .min(startOfToday(), 'Qatar Expiry Date cannot be in the past')
    .nullable(),
})

function SecurityForm({initialValues, onAfterSubmit}: SecurityFormProps) {
  const formType = initialValues ? 'update' : 'add'

  const submitHandler = async (
    values: Omit<SecurityBase, 'security_id'> | SecurityBase,
    {setSubmitting}: FormikHelpers<any>
  ) => {
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createSecurity : updateSecurity

    type DateKeys = 'license_expiry_date' | 'passport_expiry' | 'qatar_id_expiry'

    const dateFields: DateKeys[] = ['license_expiry_date', 'passport_expiry', 'qatar_id_expiry']

    dateFields.forEach((field) => {
      if (values[field] === '') {
        values[field] = null
      }
    })

    try {
      await action(values as any)
      toast.success('Success', {id: toastId})
      onAfterSubmit()
    } catch (err: any) {
      handleApiError(err, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  const formValues = initialValues || formInitialValues
  return (
    <div className="px-5 py-3">
      <Formik<Omit<SecurityBase, 'security_id'>>
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, dirty, isValid, values}) => (
          <Form>
            <Row>
              <Col md="4">
                <FormikControl
                  control="input"
                  required
                  placeholder="Security Name"
                  label="Security Name"
                  name="security_name"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    {key: 'Active', value: '1'},
                    {key: 'Inactive', value: '0'},
                  ]}
                  required
                  placeholder="Active Status"
                  label="Active Status"
                  name="security_status"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Security Code"
                  label="Security Code"
                  name="security_code"
                />
              </Col>
              <Col md="4">
                <FormikControl control="input" placeholder="Q ID" label="Q ID" name="qatar_id" />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Q ID Expiry Date"
                  label="Q ID Expiry Date"
                  name="qatar_id_expiry"
                />
              </Col>
              {/* <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="License Expiry Date"
                  label="License Expiry Date"
                  name="license_expiry_date"
                />
              </Col> */}
              <Col md="4">
                <FormikControl
                  control="select"
                  placeholder="Blood Group"
                  label="Blood Group"
                  name="blood_group"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </FormikControl>
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Passport Number"
                  label="Passport Number"
                  name="passport"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Passport Expiry Date"
                  label="Passport Expiry Date"
                  name="passport_expiry"
                />
              </Col>
              <Col md="4">
                <CountrySelect
                  placeholder="Country"
                  label="Passport Issued Country"
                  name="passport_country"
                  value={{label: values.passport_country, value: values.passport_country}} // Adjusted for compatibility
                />
              </Col>
            </Row>
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default SecurityForm
